import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ICustomWindow, IDynamicElement, WindowRefService } from '@sirius/common';
import { ICountdown } from 'shared/models';

const MILLISECONDS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 86400;
const TIME_SEGMENT_PADDING = 2;

@Component({
    selector: 'ur-countdown',
    imports: [TranslateModule],
    templateUrl: './countdown.component.html',
    styleUrl: './countdown.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountdownComponent implements OnInit, OnDestroy, IDynamicElement {
    @Input() config?: ICountdown;
    @Input() startTime = '';

    isEnabled = false;
    formattedTime = this.getFormattedTime(0);

    private readonly _windowRef = inject(WindowRefService);
    private readonly _changeDetectorRef = inject(ChangeDetectorRef);

    private intervalId: number | null = null;
    private targetTimestamp: number | null = null;

    private get Window(): ICustomWindow | undefined {
        return this._windowRef.NativeWindow;
    }

    ngOnInit(): void {
        if (this.config) {
            this.startTime = this.config.startTime;
        }

        this.targetTimestamp = this.parseTargetTimestamp(this.startTime);

        if (!this.Window || this.targetTimestamp === null || Date.now() >= this.targetTimestamp) {
            return;
        }

        this.isEnabled = true;
        this.updateRemainingTime();
        this.intervalId = this.Window.setInterval(() => this.updateRemainingTime(), MILLISECONDS_PER_SECOND);
    }

    ngOnDestroy(): void {
        this.stopTimer();
    }

    private updateRemainingTime(): void {
        if (this.targetTimestamp === null) {
            return;
        }

        const remainingMs = this.targetTimestamp - Date.now();
        if (remainingMs <= 0) {
            this.isEnabled = false;
            this.stopTimer();
            this._changeDetectorRef.markForCheck();
            return;
        }

        this.formattedTime = this.getFormattedTime(remainingMs);
        this._changeDetectorRef.markForCheck();
    }

    private stopTimer(): void {
        if (this.intervalId === null || !this.Window) {
            return;
        }

        this.Window.clearInterval(this.intervalId);
        this.intervalId = null;
    }

    private parseTargetTimestamp(isoDateString: string): number | null {
        if (!isoDateString) {
            return null;
        }

        const timestamp = new Date(isoDateString).getTime();
        return Number.isNaN(timestamp) ? null : timestamp;
    }

    private getFormattedTime(remainingMs: number) {
        const totalSeconds = Math.floor(remainingMs / MILLISECONDS_PER_SECOND);
        const days = Math.floor(totalSeconds / SECONDS_PER_DAY);
        const hours = Math.floor((totalSeconds % SECONDS_PER_DAY) / SECONDS_PER_HOUR);
        const minutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);

        return {
            days: this.padTimeSegment(days),
            hours: this.padTimeSegment(hours),
            minutes: this.padTimeSegment(minutes),
        };
    }

    private padTimeSegment(value: number): string {
        return value.toString().padStart(TIME_SEGMENT_PADDING, '0');
    }
}
